import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useApolloClient } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Spinner } from 'admin-components/components';
import { useHistory } from 'react-router';
import { GET_BOOKING } from 'store/queries';
import PropTypes from 'prop-types';
import { customerInfo as customerInfoShape, userShape } from 'constants/propTypes';
import { SectionTitleContainer, ActionsList, SubscriptionInfoSection } from '../ClubEurostarMember.styles';
import { StyledButton } from 'pages/HomePage/HomePage.styles';
import ReactTooltip from 'react-tooltip';
import { injectIntl } from 'react-intl';
import SubscriptionDetails from './SubscriptionDetails';
import { SubscriptionStatusCode } from 'constants/subscriptions';
import useFetchProfileSubscriptions from 'utils/hooks/useFetchProfileSubscription';
import { CONFIGURE_SUBSCRIPTION_RENEWAL_OVERLAP } from 'constants/launchDarkly';

import { Column, Row } from '../../../components/shared/Layout';

const Subscriptions = ({ customerInfo, canSellTravelPass, intl, user, canPerformOperations, isLoungeStaffUser }) => {
  const client = useApolloClient();
  const history = useHistory();
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState([]);
  const membershipNumber = customerInfo.profile?.loyalty?.membershipNumber;
  const customerId = customerInfo.profile?.customerId;

  const { subscriptions, loading: subscriptionLoading, error: subscriptionError } = useFetchProfileSubscriptions(customerId);

  const configureSubscriptionRenewalOverlap = useFlags()[CONFIGURE_SUBSCRIPTION_RENEWAL_OVERLAP];

  useEffect(() => {
    async function getSubscriptionData() {
      setLoadingBookings(true);

      const subscriptionBookings = await Promise.allSettled(
        subscriptions.map((subscription) =>
          client.query({
            query: GET_BOOKING,
            variables: {
              reference: subscription.bookingReference
            },
            fetchPolicy: 'network-only'
          })
        )
      );

      setSubscriptionData(
        subscriptionBookings
          .reduce((filteredSubscriptions, { status, value }, index) => {
            if (status === 'rejected') {
              console.error(`Cannot fetch a subscription booking. PNR: ${subscriptions[index].bookingReference}`);

              return filteredSubscriptions;
            }

            filteredSubscriptions.push({
              ...subscriptions[index],
              booking: value?.data?.booking ?? null
            });

            return filteredSubscriptions;
          }, [])
          .sort((a, b) => a.booking.createTimestamp > b.booking.createTimestamp)
      );

      setLoadingBookings(false);
    }

    if (subscriptions?.length > 0) {
      getSubscriptionData();
    }
  }, [client, subscriptions]);

  if (subscriptionError) {
    console.log(customerInfo.profile?.customerId);
    console.log(JSON.stringify(subscriptionError));
  }

  const shouldDisplayBuyButton = () => {
    if (!subscriptionData.length) {
      return true;
    }

    if (subscriptionData.find((s) => s.status === SubscriptionStatusCode.NOT_STARTED)) {
      return false;
    }

    const activeSub = subscriptionData.find((s) => s.status === SubscriptionStatusCode.ACTIVE);

    if (
      activeSub &&
      moment(activeSub?.endDate).diff(moment(), 'days') < configureSubscriptionRenewalOverlap &&
      !subscriptionData.find((s) => s.status === SubscriptionStatusCode.NOT_STARTED)
    ) {
      return true;
    } else if (!activeSub) {
      return true;
    }
  };

  const getBuySubscriptionMessage = () => {
    if (subscriptionData.find((s) => s.status === SubscriptionStatusCode.ACTIVE)) {
      return 'subscriptions.activeExpiringForUser';
    } else {
      return 'subscriptions.noActiveSubscriptionsForUser';
    }
  };

  const handleClick = () => {
    const activeSub = subscriptionData.find((s) => s.status === SubscriptionStatusCode.ACTIVE);
    const startDate = moment(activeSub?.endDate).add(1, 'days').unix();
    history.push(`/buy-thalys-pass?membershipNumber=${membershipNumber}${activeSub ? `&startDate=${startDate}` : ''}`);
  };

  const loading = subscriptionLoading || loadingBookings;

  return (
    <>
      {loading && (
        <Row span={16} height="10vh" data-testid="full-page-spinner">
          <Column justifyContent="center" alignItems="center">
            <Spinner />
          </Column>
        </Row>
      )}
      {!loading && (
        <SubscriptionInfoSection data-cy="subscriptions" data-testid="subscriptions-section">
          <SectionTitleContainer data-testid="subscriptions-title">
            <FormattedMessage id="subscriptions.title" tagName="h3" />
          </SectionTitleContainer>
          {shouldDisplayBuyButton() && (
            <div data-testid="subscriptions-noneActive">
              <FormattedMessage id={getBuySubscriptionMessage()} />
              <ActionsList>
                {!canSellTravelPass ? (
                  <>
                    <p
                      data-for="subscriptions-not-available-tooltip"
                      data-tip={intl.formatMessage({ id: 'subscriptions.thalys.disabledOnHoverMessage' })}
                    >
                      <StyledButton
                        disabled={true}
                        data-cy="sell-thalys-pass"
                        data-testid="subscriptions-buySubscription"
                        spaceWithin
                        onClick={handleClick}
                      >
                        <FormattedMessage id="homepage.thalyspass" />
                      </StyledButton>
                    </p>
                    <ReactTooltip id="subscriptions-not-available-tooltip" data-cy="subscriptions-not-available-tooltip" />
                  </>
                ) : (
                  <StyledButton
                    disabled={!membershipNumber || !canPerformOperations}
                    showTip={!canPerformOperations}
                    textIdTip={isLoungeStaffUser ? 'loungeStaff.restrictedAccess' : 'clubEurostar.deactivatedAccount'}
                    data-cy="sell-thalys-pass"
                    data-testid="subscriptions-buySubscription"
                    spaceWithin
                    onClick={handleClick}
                  >
                    <FormattedMessage id="homepage.thalyspass" />
                  </StyledButton>
                )}
              </ActionsList>
            </div>
          )}

          {subscriptionData.length > 0 && (
            <SubscriptionDetails
              subscriptionData={subscriptionData}
              intl={intl}
              customerInfo={customerInfo}
              user={user}
              canSellSubscription={canSellTravelPass}
            />
          )}
        </SubscriptionInfoSection>
      )}
    </>
  );
};

Subscriptions.propTypes = {
  customerInfo: customerInfoShape,
  canSellTravelPass: PropTypes.bool,
  intl: PropTypes.shape({ formatMessage: PropTypes.func }),
  user: userShape,
  canPerformOperations: PropTypes.bool,
  isLoungeStaffUser: PropTypes.bool
};

export default injectIntl(Subscriptions);
